// @ts-nocheck
import { supabase } from '@/integrations/supabase/client';
import { Notification } from '@/types/navigation';

class NotificationService {
  private static instance: NotificationService;
  private subscriptions: Map<string, any> = new Map();

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  async getUserNotifications(userId: string, userRole: 'user' | 'admin', limit = 20): Promise<Notification[]> {
    try {
      const notifications: Notification[] = [];

      // Get contact notifications for admins
      if (userRole === 'admin') {
        const { data: contacts } = await supabase
          .from('contacts')
          .select('id, subject, message, status, created_at')
          .eq('status', 'pending')
          .order('created_at', { ascending: false })
          .limit(5);

        if (contacts) {
          contacts.forEach(contact => {
            notifications.push({
              id: `contact-${contact.id}`,
              type: 'contact',
              title: 'New Contact Message',
              message: `"${contact.subject}"`,
              read: false,
              created_at: contact.created_at,
              action_url: '/admin?tab=contacts',
              user_role: 'admin',
              priority: 'medium',
              icon: 'MessageSquare'
            });
          });
        }

        // Get consignment notifications
        const { data: consignments } = await supabase
          .from('consignments')
          .select('id, artist_name, artwork_title, status, created_at')
          .eq('status', 'pending')
          .order('created_at', { ascending: false })
          .limit(5);

        if (consignments) {
          consignments.forEach(consignment => {
            notifications.push({
              id: `consignment-${consignment.id}`,
              type: 'consignment',
              title: 'New Consignment Request',
              message: `"${consignment.artwork_title}" by ${consignment.artist_name}`,
              read: false,
              created_at: consignment.created_at,
              action_url: '/admin?tab=consignments',
              user_role: 'admin',
              priority: 'medium',
              icon: 'Gavel'
            });
          });
        }
      }

      // Include explicit notifications from database table (sent via Admin > Contact Management > Notification)
      const { data: dbNotifications } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (dbNotifications) {
        dbNotifications.forEach((n: any) => {
          notifications.push({
            id: n.id,
            type: (n.type || 'general') as any,
            title: n.title || 'Notification',
            message: n.message || '',
            read: !!n.read,
            created_at: n.created_at,
            action_url: n.data?.action_url,
            user_role: 'user',
            priority: (n.data?.priority || 'medium') as any,
            icon: n.data?.icon,
            data: n.data || undefined,
          });
        });
      }

      // Get bid notifications for users (synthetic)
      const { data: userBids } = await supabase
        .from('bids')
        .select(`
          id, amount, status, created_at,
          lot_id,
          lots!inner(
            id, 
            lot_number,
            current_bid,
            auctions!inner(
              title, 
              end_date
            )
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (userBids) {
        userBids.forEach(bid => {
          const isOutbid = bid.lots?.current_bid && bid.lots.current_bid > bid.amount;
          const isWinning = bid.lots?.current_bid === bid.amount;
          
          if (isOutbid) {
            notifications.push({
              id: `bid-outbid-${bid.id}`,
              type: 'bid',
              title: 'You\'ve been outbid',
              message: `Your bid on Lot #${bid.lots?.lot_number} has been exceeded`,
              read: false,
              created_at: bid.created_at,
              action_url: `/auctions/${bid.lots?.auctions?.id}?lot=${bid.lots?.id}`,
              user_role: 'user',
              priority: 'high',
              icon: 'AlertTriangle'
            });
          } else if (isWinning) {
            notifications.push({
              id: `bid-winning-${bid.id}`,
              type: 'bid',
              title: 'You\'re winning!',
              message: `You have the highest bid on "${bid.lot?.title}"`,
              read: false,
              created_at: bid.created_at,
              action_url: `/auctions/${bid.lot?.auction?.id}?lot=${bid.lot?.id}`,
              user_role: 'user',
              priority: 'medium',
              icon: 'Trophy'
            });
          }
        });
      }

      // Get auction notifications
      const { data: upcomingAuctions } = await supabase
        .from('auctions')
        .select('id, title, start_date, status')
        .gte('start_date', new Date().toISOString())
        .lte('start_date', new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString())
        .eq('status', 'upcoming')
        .order('start_date', { ascending: true })
        .limit(5);

      if (upcomingAuctions) {
        upcomingAuctions.forEach(auction => {
          notifications.push({
            id: `auction-starting-${auction.id}`,
            type: 'auction',
            title: 'Auction Starting Soon',
            message: `"${auction.title}" starts ${new Date(auction.start_date).toLocaleDateString()}`,
            read: false,
            created_at: auction.start_date,
            action_url: `/auctions/${auction.id}`,
            user_role: 'all',
            priority: 'medium',
            icon: 'Clock'
          });
        });
      }

      // Sort by creation date and priority
      return notifications
        .sort((a, b) => {
          const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
          const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
          if (priorityDiff !== 0) return priorityDiff;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        })
        .slice(0, limit);

    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  }

  async getGuestNotifications(): Promise<Notification[]> {
    try {
      const notifications: Notification[] = [];

      // Get upcoming auctions for guests
      const { data: upcomingAuctions } = await supabase
        .from('auctions')
        .select('id, title, start_date, status')
        .gte('start_date', new Date().toISOString())
        .eq('status', 'upcoming')
        .order('start_date', { ascending: true })
        .limit(3);

      if (upcomingAuctions) {
        upcomingAuctions.forEach(auction => {
          notifications.push({
            id: `guest-auction-${auction.id}`,
            type: 'auction',
            title: 'Upcoming Auction',
            message: `"${auction.title}" - ${new Date(auction.start_date).toLocaleDateString()}`,
            read: false,
            created_at: auction.start_date,
            action_url: `/auctions/${auction.id}`,
            user_role: 'guest',
            priority: 'low',
            icon: 'Calendar'
          });
        });
      }

      // Add general announcements
      notifications.push({
        id: 'guest-welcome',
        type: 'general',
        title: 'Welcome to Florida Highwaymen Auction',
        message: 'Register to bid on authentic Florida Highwaymen artwork',
        read: false,
        created_at: new Date().toISOString(),
        action_url: '/login',
        user_role: 'guest',
        priority: 'low',
        icon: 'Info'
      });

      return notifications;
    } catch (error) {
      console.error('Error fetching guest notifications:', error);
      return [];
    }
  }

  subscribeToNotifications(userId: string, userRole: 'user' | 'admin', callback: (notifications: Notification[]) => void): void {
    const channelName = `notifications-${userId}`;
    
    if (this.subscriptions.has(channelName)) {
      this.unsubscribeFromNotifications(userId);
    }

    const channel = supabase
      .channel(channelName)
      // Database notifications for this user
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`
      }, () => {
        this.getUserNotifications(userId, userRole).then(callback);
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'contacts'
      }, () => {
        this.getUserNotifications(userId, userRole).then(callback);
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'consignments'
      }, () => {
        this.getUserNotifications(userId, userRole).then(callback);
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'bids'
      }, () => {
        this.getUserNotifications(userId, userRole).then(callback);
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'auctions'
      }, () => {
        this.getUserNotifications(userId, userRole).then(callback);
      })
      .subscribe();

    this.subscriptions.set(channelName, channel);
  }

  unsubscribeFromNotifications(userId: string): void {
    const channelName = `notifications-${userId}`;
    const channel = this.subscriptions.get(channelName);
    
    if (channel) {
      supabase.removeChannel(channel);
      this.subscriptions.delete(channelName);
    }
  }

  async markNotificationAsRead(notificationId: string): Promise<void> {
    try {
      // Try to mark persisted notification row as read
      try {
        const { error } = await supabase
          .from('notifications')
          .update({ read: true })
          .eq('id', notificationId);
        if (error) {
          console.warn('DB mark read failed or not a DB notification, will fallback to localStorage:', error.message);
        }
      } catch (e) {
        // ignore and fallback
      }

      // Also maintain synthetic/local read state for generated notifications
      const readNotifications = JSON.parse(localStorage.getItem('read_notifications') || '[]');
      if (!readNotifications.includes(notificationId)) {
        readNotifications.push(notificationId);
        localStorage.setItem('read_notifications', JSON.stringify(readNotifications));
        console.log(`Marked notification ${notificationId} as read in localStorage`);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }

  async markAllNotificationsAsRead(userId: string, userRole: 'user' | 'admin'): Promise<void> {
    try {
      // Update DB notifications for this user
      try {
        await supabase
          .from('notifications')
          .update({ read: true })
          .eq('user_id', userId)
          .eq('read', false);
      } catch (e) {
        console.warn('DB mark all read failed:', (e as any)?.message);
      }

      const notifications = await this.getUserNotifications(userId, userRole);
      const readNotifications = JSON.parse(localStorage.getItem('read_notifications') || '[]');
      
      notifications.forEach(notification => {
        if (!readNotifications.includes(notification.id)) {
          readNotifications.push(notification.id);
        }
      });
      
      localStorage.setItem('read_notifications', JSON.stringify(readNotifications));
      console.log(`Marked ${notifications.length} notifications as read in localStorage`);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }

  isNotificationRead(notificationId: string): boolean {
    const readNotifications = JSON.parse(localStorage.getItem('read_notifications') || '[]');
    return readNotifications.includes(notificationId);
  }
}

export const notificationService = NotificationService.getInstance(); 